import { parseLineEquation as parseSharedLineEquation, equivalentLines } from "../../../utils/lineEquation";
import React from "react";
import { BlockMath, InlineMath } from "react-katex";

import EquationInput from "../../../components/inputs/EquationInput";
import FractionInput, { formatLatexPreview } from "../../../components/inputs/FractionInput";
import SelectInput from "../../../components/inputs/SelectInput";
import SqrtInput from "../../../components/inputs/SqrtInput";
import { previewMathExpression } from "../../../components/inputs/mathExpression";
import { formatApproximateMixedFractionLatex as formatNumber } from "../../../utils/fractionLatex";

const YES = "YES";
const NO = "NO";
const EPSILON = 1e-6;
const YES_NO = [{ value: YES, label: "Tak" }, { value: NO, label: "Nie" }];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (items) => items[Math.floor(Math.random() * items.length)];
const point = ([x, y]) => `(${x},${y})`;
const wrap = (value) => (value < 0 ? `(${value})` : `${value}`);

function determinant(a, b, c) {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

function distanceSquared(a, b) {
  return (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2;
}

function randomTriangle(limit = 6) {
  let result;
  do {
    result = Array.from({ length: 3 }, () => [randInt(-limit, limit), randInt(-limit, limit)]);
  } while (determinant(...result) === 0);
  return result;
}

function numericInput(label, value, onChange, disabled) {
  return (
    <div className="flex items-center gap-2">
      <span><InlineMath math={`${label}=`} /></span>
      <FractionInput value={value || ""} onChange={onChange} disabled={disabled} label={label} />
    </div>
  );
}

const parsed = (value) => value?.parsed;
const close = (actual, expected) => Number.isFinite(actual) && Math.abs(actual - expected) < EPSILON;
const latexNumber = (value) => formatNumber(value);

function lineLatex(slope, intercept) {
  const slopePart = slope === 0 ? "" : slope === 1 ? "x" : slope === -1 ? "-x" : `${latexNumber(slope)}x`;
  if (intercept === 0) return `y=${slopePart || "0"}`;
  return `y=${slopePart}${intercept > 0 && slopePart ? "+" : ""}${latexNumber(intercept)}`;
}

export function generateAreaTask() {
  const [a, b, c] = randomTriangle();
  const doubleArea = Math.abs(determinant(a, b, c));
  return { type: "TriangleAreaCoordinateTask", data: { a, b, c }, answer: doubleArea / 2 };
}

export function generateMedianTask() {
  let a;
  let b;
  let c;
  let midpoint;
  do {
    midpoint = [randInt(-4, 4), randInt(-4, 4)];
    const [dx, dy] = pick([[1, 2], [2, 1], [2, 3], [3, 1], [-1, 2], [2, -1]]);
    b = [midpoint[0] + dx, midpoint[1] + dy];
    c = [midpoint[0] - dx, midpoint[1] - dy];
    a = [randInt(-7, 7), randInt(-7, 7)];
  } while (a[0] === midpoint[0] || determinant(a, b, c) === 0);
  const slope = (midpoint[1] - a[1]) / (midpoint[0] - a[0]);
  const intercept = a[1] - slope * a[0];
  const lineA = midpoint[1] - a[1];
  const lineB = a[0] - midpoint[0];
  const lineC = midpoint[0] * a[1] - a[0] * midpoint[1];
  return {
    type: "TriangleMedianEquationTask",
    data: { a, b, c, midpoint },
    answer: { slope, intercept, lineA, lineB, lineC },
  };
}

export function generateCentroidTask() {
  let a;
  let b;
  let c;
  let centroid;
  do {
    centroid = [randInt(-3, 3), randInt(-3, 3)];
    a = [randInt(-6, 6), randInt(-6, 6)];
    b = [randInt(-6, 6), randInt(-6, 6)];
    c = [3 * centroid[0] - a[0] - b[0], 3 * centroid[1] - a[1] - b[1]];
  } while (determinant(a, b, c) === 0 || Math.max(...c.map(Math.abs)) > 12);
  return { type: "TriangleCentroidTask", data: { a, b, c }, answer: centroid };
}

function rightTriangle() {
  const a = [randInt(-4, 4), randInt(-4, 4)];
  const [ux, uy] = pick([[2, 1], [3, 1], [3, 2], [4, 1], [1, 3]]);
  const scale = pick([1, 2]);
  return [a, [a[0] + ux, a[1] + uy], [a[0] - scale * uy, a[1] + scale * ux]];
}

function nonRightTriangle() {
  let result;
  let sides;
  do {
    result = randomTriangle();
    sides = [distanceSquared(result[0], result[1]), distanceSquared(result[1], result[2]), distanceSquared(result[2], result[0])].sort((x, y) => x - y);
  } while (sides[0] + sides[1] === sides[2]);
  return result;
}

export function generateRightTriangleTask() {
  const isRight = Math.random() < 0.5;
  const [a, b, c] = isRight ? rightTriangle() : nonRightTriangle();
  const squaredSides = [distanceSquared(a, b), distanceSquared(b, c), distanceSquared(c, a)].sort((x, y) => x - y);
  return { type: "TriangleRightAngleCheckTask", data: { a, b, c, squaredSides, isRight }, answer: isRight ? YES : NO };
}

const CIRCLE_VECTORS = {
  5: [[5, 0], [3, 4], [0, 5], [-3, 4], [-5, 0], [-3, -4], [0, -5], [3, -4]],
  10: [[10, 0], [6, 8], [0, 10], [-6, 8], [-10, 0], [-6, -8], [0, -10], [6, -8]],
  13: [[13, 0], [5, 12], [0, 13], [-5, 12], [-13, 0], [-5, -12], [0, -13], [5, -12]],
};

export function generateCircumcenterTask() {
  const radius = pick([5, 10, 13]);
  const center = [randInt(-3, 3), randInt(-3, 3)];
  let vertices;
  do {
    vertices = [...CIRCLE_VECTORS[radius]].sort(() => Math.random() - 0.5).slice(0, 3)
      .map(([x, y]) => [center[0] + x, center[1] + y]);
  } while (determinant(...vertices) === 0);
  return { type: "TriangleCircumcenterTask", data: { a: vertices[0], b: vertices[1], c: vertices[2] }, answer: { center, radius } };
}

function verticesLatex(data) {
  return `A=${point(data.a)},\\quad B=${point(data.b)},\\quad C=${point(data.c)}`;
}

export function renderTask({ type, data }) {
  const vertices = <InlineMath math={verticesLatex(data)} />;
  if (type === "TriangleAreaCoordinateTask") return <p>Dane są wierzchołki {vertices}. Oblicz pole trójkąta <InlineMath math="ABC" />.</p>;
  if (type === "TriangleMedianEquationTask") return <p>Dane są wierzchołki {vertices}. Wyznacz równanie środkowej poprowadzonej z wierzchołka <InlineMath math="A" />. Odpowiedź możesz podać w postaci kierunkowej albo ogólnej.</p>;
  if (type === "TriangleCentroidTask") return <p>Dane są wierzchołki {vertices}. Wyznacz współrzędne środka ciężkości trójkąta.</p>;
  if (type === "TriangleRightAngleCheckTask") return <p>Dane są wierzchołki {vertices}. Sprawdź, czy trójkąt <InlineMath math="ABC" /> jest prostokątny.</p>;
  if (type === "TriangleCircumcenterTask") return <p>Dane są wierzchołki {vertices}. Wyznacz środek i promień okręgu opisanego na trójkącie.</p>;
  return null;
}

export function renderInput({ type }, userAnswer, onChange, disabled) {
  if (type === "TriangleAreaCoordinateTask") return numericInput("P", userAnswer?.area, (area) => onChange({ ...userAnswer, area }), disabled);
  if (type === "TriangleMedianEquationTask") return <div style={{ maxWidth: 520 }}><p>Równanie środkowej (np. <InlineMath math={String.raw`y=-\frac12x+3`} /> lub <InlineMath math="x+2y-6=0" />):</p><EquationInput value={userAnswer?.line || ""} onChange={(line) => onChange({ ...userAnswer, line })} disabled={disabled} placeholder="np. y=-1/2x+3" basis="440px" /></div>;
  if (type === "TriangleCentroidTask") return <div className="flex flex-col gap-2">{numericInput("x_G", userAnswer?.x, (x) => onChange({ ...userAnswer, x }), disabled)}{numericInput("y_G", userAnswer?.y, (y) => onChange({ ...userAnswer, y }), disabled)}</div>;
  if (type === "TriangleRightAngleCheckTask") return <SelectInput value={typeof userAnswer === "string" ? userAnswer : ""} onChange={(event) => onChange(event?.target?.value || "")} disabled={disabled} options={YES_NO} />;
  if (type === "TriangleCircumcenterTask") return <div className="flex flex-col gap-2">{numericInput("x_O", userAnswer?.x, (x) => onChange({ ...userAnswer, x }), disabled)}{numericInput("y_O", userAnswer?.y, (y) => onChange({ ...userAnswer, y }), disabled)}<div className="flex items-center gap-2"><span><InlineMath math="r=" /></span><SqrtInput value={userAnswer?.r || ""} onChange={(r) => onChange({ ...userAnswer, r })} disabled={disabled} label="r" /></div></div>;
  return null;
}



function parseLineEquation(raw) { return parseSharedLineEquation(raw, { implicitZero: true }); }



export function checkAnswer({ type, answer }, userAnswer) {
  if (type === "TriangleAreaCoordinateTask") return close(parsed(userAnswer?.area), answer);
  if (type === "TriangleMedianEquationTask") {
    const line = parseLineEquation(userAnswer?.line);
    return Boolean(line && equivalentLines(line, { A: answer.lineA, B: answer.lineB, C: answer.lineC }));
  }
  if (type === "TriangleCentroidTask") return close(parsed(userAnswer?.x), answer[0]) && close(parsed(userAnswer?.y), answer[1]);
  if (type === "TriangleRightAngleCheckTask") return userAnswer === answer;
  if (type === "TriangleCircumcenterTask") return close(parsed(userAnswer?.x), answer.center[0]) && close(parsed(userAnswer?.y), answer.center[1]) && close(parsed(userAnswer?.r), answer.radius);
  return false;
}

export function renderAnswer({ type, answer }) {
  if (type === "TriangleAreaCoordinateTask") return <InlineMath math={`P=${latexNumber(answer)}`} />;
  if (type === "TriangleMedianEquationTask") return <InlineMath math={lineLatex(answer.slope, answer.intercept)} />;
  if (type === "TriangleCentroidTask") return <InlineMath math={`G=${point(answer)}`} />;
  if (type === "TriangleRightAngleCheckTask") return <b>{answer === YES ? "Tak" : "Nie"}</b>;
  if (type === "TriangleCircumcenterTask") return <InlineMath math={`O=${point(answer.center)},\\quad r=${answer.radius}`} />;
  return null;
}

export function renderUserAnswer(userAnswer, task) {
  if (task.type === "TriangleRightAngleCheckTask") return userAnswer === YES || userAnswer === NO ? <span>{userAnswer === YES ? "Tak" : "Nie"}</span> : <i>(brak odpowiedzi)</i>;
  if (task.type === "TriangleMedianEquationTask") return userAnswer?.line ? <InlineMath math={userAnswer.line} /> : <i>(brak odpowiedzi)</i>;
  if (task.type === "TriangleCircumcenterTask") {
    const entries = [
      userAnswer?.x?.raw ? <div key="x"><InlineMath math={formatLatexPreview(userAnswer.x.raw, "x_O")} /></div> : null,
      userAnswer?.y?.raw ? <div key="y"><InlineMath math={formatLatexPreview(userAnswer.y.raw, "y_O")} /></div> : null,
      userAnswer?.r?.raw ? <div key="r"><InlineMath math={`r=${previewMathExpression(userAnswer.r.raw) || userAnswer.r.raw}`} /></div> : null,
    ].filter(Boolean);
    return entries.length ? <>{entries}</> : <i>(brak odpowiedzi)</i>;
  }
  const fields = task.type === "TriangleAreaCoordinateTask" ? [["P", userAnswer?.area]] : task.type === "TriangleCentroidTask" ? [["x_G", userAnswer?.x], ["y_G", userAnswer?.y]] : [["x_O", userAnswer?.x], ["y_O", userAnswer?.y], ["r", userAnswer?.r]];
  const available = fields.filter(([, value]) => value?.raw);
  return available.length ? <>{available.map(([label, value]) => <div key={label}><InlineMath math={formatLatexPreview(value.raw, label)} /></div>)}</> : <i>(brak odpowiedzi)</i>;
}

export function renderExplanation({ type, data, answer }) {
  if (type === "TriangleAreaCoordinateTask") return <div><p>Korzystamy ze wzoru wyznacznikowego:</p><BlockMath math={`P=\\frac12\\left|(${data.b[0]}-${wrap(data.a[0])})(${data.c[1]}-${wrap(data.a[1])})-(${data.b[1]}-${wrap(data.a[1])})(${data.c[0]}-${wrap(data.a[0])})\\right|=${latexNumber(answer)}`} /></div>;
  if (type === "TriangleMedianEquationTask") return <div><p>Wyznaczamy środek boku <InlineMath math="BC" />:</p><BlockMath math={`S=\\left(\\frac{${data.b[0]}+${wrap(data.c[0])}}2,\\frac{${data.b[1]}+${wrap(data.c[1])}}2\\right)=${point(data.midpoint)}`} /><p>Środkowa przechodzi przez <InlineMath math={`A=${point(data.a)}`} /> i <InlineMath math={`S=${point(data.midpoint)}`} />.</p><BlockMath math={lineLatex(answer.slope, answer.intercept)} /></div>;
  if (type === "TriangleCentroidTask") return <div><p>Uśredniamy osobno współrzędne wierzchołków:</p><BlockMath math={`G=\\left(\\frac{${data.a[0]}+${wrap(data.b[0])}+${wrap(data.c[0])}}3,\\frac{${data.a[1]}+${wrap(data.b[1])}+${wrap(data.c[1])}}3\\right)=${point(answer)}`} /></div>;
  if (type === "TriangleRightAngleCheckTask") { const [a, b, c] = data.squaredSides; return <div><p>Porównujemy kwadraty długości boków:</p><BlockMath math={`${a},\\ ${b},\\ ${c}`} /><BlockMath math={`${a}+${b}${data.isRight ? "=" : "\\neq"}${c}`} /><p>{data.isRight ? "Równość Pitagorasa jest spełniona, więc trójkąt jest prostokątny." : "Równość Pitagorasa nie jest spełniona, więc trójkąt nie jest prostokątny."}</p></div>; }
  if (type === "TriangleCircumcenterTask") return <div><p>Punkt przecięcia symetralnych dwóch boków jest środkiem okręgu opisanego:</p><BlockMath math={`O=${point(answer.center)}`} /><p>Promień to odległość środka od wierzchołka:</p><BlockMath math={`r=|OA|=\\sqrt{(${data.a[0]}-${wrap(answer.center[0])})^2+(${data.a[1]}-${wrap(answer.center[1])})^2}=${answer.radius}`} /></div>;
  return null;
}
